import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import { Building2, Calendar, Trophy, Eye, AlertTriangle } from 'lucide-react';

import { getCertificationsByUser } from '../../../services/employeeService';
import { formatDate } from '../../../utils/Utils';
import Tooltip from '../../../components/ui/Tooltip';
import DocumentViewerModal from '../../../components/ui/DocumentViewerModal';

const isExpired = (expiryDate) => {
  if (!expiryDate) return false;
  return new Date(expiryDate) < new Date();
};

const isExpiringSoon = (expiryDate) => {
  if (!expiryDate) return false;
  const now = new Date();
  const expiry = new Date(expiryDate);
  const diffDays = (expiry - now) / (1000 * 60 * 60 * 24);
  return diffDays > 0 && diffDays <= 30;
};

export default function EmployeeCertificationsTab({ employeeId }) {
  const { t, i18n } = useTranslation();
  const lang = i18n.language;

  const [certifications, setCertifications] = useState([]);
  const [loading, setLoading] = useState(true);
  // { fileUrl, title } | null — açık olduğunda DocumentViewerModal'ı besler. Beş özlük-dosyası
  // yüzeyinin AYNI deseni (bkz. EmployeePersonnelFileTab.js): belge artık yeni sekmede DEĞİL
  // uygulama-içi modalda açılır (ürün sahibi kararı), bu sekme daha önce bu göçü kaçırmıştı.
  const [viewing, setViewing] = useState(null);

  useEffect(() => {
    setLoading(true);
    getCertificationsByUser(employeeId, { pageSize: 100 })
      .then((res) => {
        const data = res.data?.data?.data || res.data?.data || [];
        setCertifications(data);
      })
      .catch(() => setCertifications([]))
      .finally(() => setLoading(false));
  }, [employeeId]);

  if (loading) return <LoadingText>{t('common.loading') || '...'}</LoadingText>;
  if (certifications.length === 0) return <EmptyText>{t('common.noRecords') || '-'}</EmptyText>;

  return (
    <Container>
      <CardGrid>
        {certifications.map((cert) => {
          const expired = isExpired(cert.expiryDate);
          const expiringSoon = isExpiringSoon(cert.expiryDate);

          return (
            <CertCard key={cert.id} $expired={expired}>
              <CertHeader>
                <CertName>{cert.name || '-'}</CertName>
                {cert.categoryName && (
                  <CategoryTag>{cert.categoryName}</CategoryTag>
                )}
              </CertHeader>

              {expired && (
                <WarningBadge $color="#ef4444" $bg="#fef2f2">
                  <AlertTriangle size={12} />
                  {t('certification.expired') || 'Expired'}
                </WarningBadge>
              )}
              {expiringSoon && !expired && (
                <WarningBadge $color="#ea580c" $bg="#fff7ed">
                  <AlertTriangle size={12} />
                  {t('certification.expiringSoon') || 'Expiring soon'}
                </WarningBadge>
              )}

              <CertDetails>
                {cert.institution && (
                  <DetailRow>
                    <Building2 size={14} />
                    <span>{cert.institution}</span>
                  </DetailRow>
                )}
                <DetailRow>
                  <Calendar size={14} />
                  <span>
                    {formatDate(cert.issueDate, lang)}
                    {cert.expiryDate ? ` - ${formatDate(cert.expiryDate, lang)}` : ''}
                  </span>
                </DetailRow>
                {cert.score && (
                  <DetailRow>
                    <Trophy size={14} />
                    <span>{cert.score}</span>
                  </DetailRow>
                )}
              </CertDetails>

              {cert.documentUrl && (
                <DocLink>
                  <Tooltip text={t('certification.viewDocument') || 'View document'}>
                    <DocButton
                      onClick={() => setViewing({ fileUrl: cert.documentUrl, title: cert.name })}
                    >
                      <Eye size={14} />
                    </DocButton>
                  </Tooltip>
                </DocLink>
              )}
            </CertCard>
          );
        })}
      </CardGrid>
      <DocumentViewerModal
        open={!!viewing}
        fileUrl={viewing?.fileUrl}
        title={viewing?.title}
        onClose={() => setViewing(null)}
      />
    </Container>
  );
}

const Container = styled.div`
  padding: 8px 0;
`;

const LoadingText = styled.div`
  font-family: 'Nunito Sans', sans-serif;
  font-size: 14px;
  color: #A0A8B4;
  padding: 40px;
  text-align: center;
`;

const EmptyText = styled.div`
  font-family: 'Nunito Sans', sans-serif;
  font-size: 14px;
  color: #A0A8B4;
  padding: 40px;
  text-align: center;
`;

const CardGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(320px, 1fr));
  gap: 16px;
`;

const CertCard = styled.div`
  padding: 20px;
  background: #fff;
  border-radius: 6px;
  border: 1px solid ${({ $expired }) => $expired ? '#fca5a5' : '#E8ECF0'};
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const CertHeader = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 8px;
`;

const CertName = styled.span`
  font-family: 'Nunito Sans', sans-serif;
  font-size: 15px;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.text.primary};
`;

const CategoryTag = styled.span`
  font-family: 'Nunito Sans', sans-serif;
  font-size: 11px;
  font-weight: 600;
  padding: 2px 8px;
  border-radius: 4px;
  background: #f1f5f9;
  color: #64748b;
  white-space: nowrap;
`;

const WarningBadge = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  font-family: 'Nunito Sans', sans-serif;
  font-size: 12px;
  font-weight: 600;
  padding: 3px 8px;
  border-radius: 4px;
  background: ${({ $bg }) => $bg};
  color: ${({ $color }) => $color};
  align-self: flex-start;
`;

const CertDetails = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

const DetailRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  font-family: 'Nunito Sans', sans-serif;
  font-size: 13px;
  color: #64748b;
`;

const DocLink = styled.div`
  display: flex;
  justify-content: flex-end;
`;

const DocButton = styled.button`
  background: none;
  border: 1px solid #E8ECF0;
  border-radius: 4px;
  padding: 6px 10px;
  cursor: pointer;
  color: ${({ theme }) => theme.colors.primary.main};
  display: flex;
  align-items: center;

  &:hover {
    background: ${({ theme }) => theme.colors.bgColor || '#F5F7FA'};
  }
`;
